




























































































































import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import { ApiError } from '@/shared/services/baseApiClient';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import Toast from '@/shared/services/toast';
import TitledSection from '@/shared/components/TitledSection.vue';
import Banner from '@/shared/components/Banner.vue';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import { botsApi } from '../services/bots';
import { botAppSerivce } from '../services/botAppService';
import {
	BotActionAppViewModel,
	BotConfigurationViewModel,
	FollowUpActionType,
	FollowUpActionViewModel,
} from '@/features/bots/models';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import TagsInput from '@/shared/components/TagsInput.vue';
import TriageIcon from '@/shared/components/TriageIcon.vue';
import { ConfiguredAppIntegration } from '../../apps/shared/models/configuredAppIntegration';
import { RuleViewModel } from '../models/ruleViewModel';
import RulesList from '@/features/bots/components/rules/RulesList.vue';
import { RuleConditionsManager } from '@/features/bots/components/rules/ruleConditionsManager';
import { ConditionDataType } from '@/shared/components/conditions/models/conditions';

@Component({
	components: {
		PageHeader,
		Banner,
		LoadingSpinner,
		TitledSection,
		TagsInput,
		CapableAppPicker,
		TriageIcon,
		RulesList,
	},
})
export default class Tasks extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	public botAppLoaded: boolean = false;
	public botAppInstallError: boolean = false;

	public updateInProgress: boolean = false;
	public reinstallInProgress: boolean = false;
	public enableInProgress: boolean = false;

	public viewModel: BotConfigurationViewModel = {
		disabled: false,
		captureName: true,
		captureEmail: true,
		captureEnquiry: false,
		enquiryOptions: [],
		rules: [],
		followUpActions: [],
	};

	public ruleConditionsManager: RuleConditionsManager = new RuleConditionsManager();

	public created() {
		if (!this.currentWorkspace) {
			this.handleSeriousError('No workspace selected');
			return;
		}

		this.ruleConditionsManager.setContextualConditions([
			{
				id: 'flow.route',
				title: 'Enquiry Topic',
				type: ConditionDataType.Text,
				icon: 'QuestionIcon',
			},
			{
				id: 'appUser.currentUrl',
				title: 'Current Page URL',
				type: ConditionDataType.Text,
				icon: 'PageIcon',
			},
		]);

		botsApi
			.get(this.currentWorkspace.id)
			.then((botResponse: BotConfigurationViewModel) => {
				this.initBotApp(botResponse);
				this.botAppLoaded = true;
			})
			.catch((error: ApiError) => {
				if (error.status === 404) {
					this.botAppInstallError = true;
					this.botAppLoaded = true;
					return;
				}
				this.handleSeriousError(error.message);
			});
	}

	public configRequest(
		workspaceId: string,
		installedAppId: string,
		values: any,
		previousValues: any,
		componentId: any
	) {
		return botsApi.botAppConfig(
			workspaceId,
			installedAppId,
			values,
			previousValues,
			componentId
		);
	}

	public getSelectedFollowUpApps(): Array<
		ConfiguredAppIntegration | undefined
	> {
		return this.viewModel.followUpActions
			.filter(
				(action) => action.type === FollowUpActionType.AppIntegration
			)
			.filter((action) => action.app !== undefined)
			.map((action) => action.app);
	}

	public rulesUpdated(rules: RuleViewModel[]) {
		this.viewModel.rules = rules;
	}

	public selectedFollowUpActionsChanged(
		selectedApps: BotActionAppViewModel[]
	) {
		const nonAppActions = this.viewModel.followUpActions.filter(
			(x) => x.type !== FollowUpActionType.AppIntegration
		);
		const appActions = selectedApps.map<FollowUpActionViewModel>((app) => ({
			type: FollowUpActionType.AppIntegration,
			app,
		}));
		this.viewModel.followUpActions = [...appActions, ...nonAppActions];
	}

	public reinstall() {
		if (this.reinstallInProgress) {
			return;
		}

		this.reinstallInProgress = true;

		botAppSerivce
			.fullInstall(this.currentWorkspace.id)
			.then((res) => {
				this.initBotApp(res);
				Toast.showSuccess('Bots have been successfully configured');
				this.botAppInstallError = false;
			})
			.catch((error) => {
				Toast.showError(error.message);
			})
			.finally(() => {
				this.reinstallInProgress = false;
			});
	}

	public save() {
		if (
			this.updateInProgress ||
			!this.botAppLoaded ||
			this.botAppInstallError
		) {
			return;
		}

		if (this.ruleList.areRulesAreValid()) {
			this.updateInProgress = true;
			botsApi
				.update(this.currentWorkspace.id, this.viewModel)
				.then(() => {
					Toast.showSuccess('Successfully updated Bot');
				})
				.catch((err) => {
					Toast.showError(err.message);
				})
				.finally(() => {
					this.updateInProgress = false;
				});
		} else {
			Toast.showError('Please fix the errors displayed before saving');
		}
	}

	public enableBot() {
		if (this.enableInProgress) {
			return;
		}

		this.enableInProgress = true;

		// todo: make a copy of the viewModel on init and on save
		// then use that version of the viewModel in this request
		// so that other options aren't saved by enable or disable
		this.viewModel.disabled = false;

		botAppSerivce
			.enableBot(this.currentWorkspace, this.viewModel)
			.then(() => {
				Toast.showSuccess('Bots have been enabled');
				this.viewModel.disabled = false;
			})
			.catch((err) => {
				Toast.showError(err.message);
			})
			.finally(() => {
				this.enableInProgress = false;
			});
	}

	public disableBot() {
		if (!this.botAppLoaded) {
			return;
		}

		if (confirm('Are you sure you want to disable bots?')) {
			// see related todo in enableBot
			this.viewModel.disabled = true;

			botAppSerivce
				.disableBot(this.currentWorkspace, this.viewModel)
				.then(() => {
					Toast.showSuccess('Bots have been disabled');
					this.viewModel.disabled = true;
				})
				.catch((err) => {
					Toast.showError(err.message);
				});
		}
	}

	private initBotApp(config: BotConfigurationViewModel) {
		this.viewModel = config;
	}

	private get ruleList(): RulesList {
		return this.$refs.rulesList as RulesList;
	}
}
